import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "../config/axios";
import PageWrapper from "../components/common/PageWrapper";
import Header from "../components/common/Header";
import BodyWrapper from "../components/common/BodyWrapper";
import Footer from "../components/common/Footer";
import ListingComponent from "../components/careersPartials/ListingComponent";
import { useGeneral } from "../components/contexts/GeneralContext";
import GeneralButton from "../components/common/GeneralButton";
import FullScreenLoading from "../components/common/loading/FullScreenLoading";
import ApiErrorMessage from "../components/common/ApiErrorMessage";
import NoItemsFound from "../components/common/NoItemsFound";
import SearchBar from "../components/common/SearchBar";

const usePageContent = (page, query) => {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState({ items: [], pagination: {} });
  const [error, setError] = useState("");
  const { discountCoupons } = useGeneral();

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`/jobs?page=${page}&q=${query}`);
        if (active) {
          setContent((prev) => ({
            items: page === 1 ? data.items : [...prev.items, ...data.items],
            pagination: data.pagination,
          }));
        }
      } catch (err) {
        console.error(err);
        if (active) setError("Failed to load jobs. Please try again later.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [page, query]);

  return { content, loading, error, discountCoupons };
};
const useScrollToTop = () => {
  useEffect(() => {
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
    return () => clearTimeout(timer);
  }, []);
};
const CareersListingPage = () => {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const { content, loading, error, discountCoupons } = usePageContent(
    page,
    query
  );
  useScrollToTop();
  const loadMore = useCallback(() => {
    if (page < content.pagination.pageCount) {
      setPage((prev) => prev + 1);
    }
  }, [page, content.pagination.pageCount]);

  const hasMore = useMemo(
    () => page < content.pagination.pageCount,
    [page, content.pagination.pageCount]
  );
  const handleSearch = useCallback((searchQuery) => {
    setPage(1);
    setQuery(searchQuery);
  }, []);

  const handleClearSearch = useCallback(() => {
    setInputValue("");
    handleSearch("");
  }, [handleSearch]);
  const isSingleItem = content.items.length === 1;
  const hasItems = content?.pagination?.totalItems > 0;
  const hasQuery = !!query;
  return (
    <>
      {loading && page === 1 && <FullScreenLoading />}
      <PageWrapper loading={loading} className="bg-bg min-h-screen">
        <Header code={discountCoupons} />
        <BodyWrapper
          loading={loading}
          className="min-h-[400px] flex flex-col gap-4 lg:gap-6"
        >
          <>
            {hasItems && (
              <div className="flex flex-col items-start md:items-center justify-between gap-2 2xl:gap-4 mt-2 lg:mt-0 ">
                <div className="flex flex-col md:flex-row md:items-center justify-start lg:justify-between gap-2 w-full">
                  <div className="flex-1 flex items-center justify-end gap-2 w-full">
                    <SearchBar
                      value={inputValue}
                      setValue={setInputValue}
                      onClear={handleClearSearch}
                      onSearch={() => handleSearch(inputValue)}
                    />
                  </div>
                </div>

                {hasQuery && (
                  <div className="flex items-center flex-wrap gap-2 px-3 py-2 bg-[#D06F36]/10 rounded-lg w-full">
                    <p className="text-sm text-gray-700">
                      Showing results for{" "}
                      <span className="text-[#D06F36]">"{query}"</span>
                    </p>
                    <button
                      onClick={handleClearSearch}
                      title="Click to remove applied search"
                      className="ml-auto text-xs text-[#D06F36] font-medium hover:underline transition-all duration-200"
                    >
                      Clear All
                    </button>
                  </div>
                )}
              </div>
            )}
            {!loading && error ? (
              <ApiErrorMessage
                message1="Failed to load jobs"
                message2="Please check your connection and try again."
              />
            ) : !loading && !hasItems ? (
              <div className="flex items-center justify-center my-6 1xl:my-4">
                <img
                  src={require("../assets/images/comingSoon.png")}
                  alt="shadow"
                  className="w-full h-full xl:max-w-[40%]"
                />
              </div>
            ) : !loading &&
              hasItems &&
              content?.pagination?.totalFilteredItems === 0 ? (
              <NoItemsFound
                message1="No jobs found"
                message2="Please try a different search term."
              />
            ) : (
              <div className=" rounded-[15px] bg-[#DEE2DD]/40 p-6 md:px-10 lg:px-14 lg:py-10 2xl:p-12 2xl:px-20 relative ">
                <p className="text-34pt lg:text-36pt mb-4">Careers</p>
                <div className="flex flex-col gap-6  lg:gap-10 2xl:gap-12">
                  <div
                    className={`grid gap-4 md:gap-4 lg:gap-8 ${
                      isSingleItem ? "grid-cols-1" : "1xl:grid-cols-2 "
                    }`}
                  >
                    {content?.items
                      .sort(
                        (a, b) =>
                          new Date(b.postedDate) - new Date(a.postedDate)
                      )
                      .map((item, index) => (
                        <ListingComponent key={index} item={item} />
                      ))}
                  </div>

                  {hasMore && (
                    <div className="flex justify-center">
                      <GeneralButton
                        text="Load More"
                        title="Click to load more items"
                        onClick={loadMore}
                        bgColor="#D06F36"
                      />
                    </div>
                  )}
                  {loading && page >= 1 && (
                    <div className="flex justify-center mt-4">
                      <div className="w-5 h-5 border-4 border-tertiary border border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        </BodyWrapper>
        <Footer />
      </PageWrapper>
    </>
  );
};

export default CareersListingPage;
